/**
 * Client-side token-bucket rate limiter. Used by source modules that must
 * batch requests (e.g. e-hentai's `api.php` allows ~5 parallel + 5s between
 * batches per gallery, 25 gids per request).
 *
 * Not thread-safe across tabs — for that we'd need a `BroadcastChannel`-based
 * implementation. Per-tab is enough for MVP.
 */
export interface RateLimiterOpts {
  /** Maximum burst size. */
  capacity: number;
  /** Tokens added per second. */
  refillPerSec: number;
}

export class RateLimiter {
  private readonly capacity: number;
  private readonly refillPerSec: number;
  private tokens: number;
  private lastRefill: number;
  /** FIFO queue of waiters; resolved when a token is available. */
  private waiters: Array<{ resolve: () => void; n: number }> = [];

  constructor(opts: RateLimiterOpts) {
    this.capacity = Math.max(1, opts.capacity);
    this.refillPerSec = Math.max(0, opts.refillPerSec);
    this.tokens = this.capacity;
    this.lastRefill = Date.now();
  }

  private refill(): void {
    const now = Date.now();
    const elapsedSec = (now - this.lastRefill) / 1000;
    if (elapsedSec <= 0) return;
    this.tokens = Math.min(this.capacity, this.tokens + elapsedSec * this.refillPerSec);
    this.lastRefill = now;
  }

  /** Block until `n` tokens are available, then consume them. */
  async take(n = 1): Promise<void> {
    this.refill();
    if (this.tokens >= n) {
      this.tokens -= n;
      return;
    }
    await new Promise<void>((resolve) => {
      this.waiters.push({ resolve, n });
      this.schedulePump();
    });
  }

  private pumpScheduled = false;

  private schedulePump(): void {
    if (this.pumpScheduled) return;
    this.pumpScheduled = true;
    const waitMs = this.refillPerSec > 0 ? Math.ceil((1 / this.refillPerSec) * 1000) : 1000;
    setTimeout(() => {
      this.pumpScheduled = false;
      this.pump();
    }, waitMs);
  }

  private pump(): void {
    this.refill();
    while (this.waiters.length > 0) {
      const head = this.waiters[0];
      if (!head) break;
      if (this.tokens < head.n) break;
      this.tokens -= head.n;
      this.waiters.shift();
      head.resolve();
    }
    if (this.waiters.length > 0) this.schedulePump();
  }
}

/** Convenience: `await sleep(ms)`. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
